import Common from '@/libs/common';

export type AlphabeticalEntry<T> = { type: 'separator'; value: string } | { type: 'item'; value: T };

const ALPHABETICAL_FIRST_LETTER = /^[A-ZÀ-ÖØ-öø-ÿ]$/;

/**
 * Flatten items (already sorted by normalized name) into letter separators and
 * items, with a leading "#" section collecting names that do not start with a
 * letter. The flat shape is what the incremental loading slices.
 */
export function flattenAlphabetical<T>(items: T[], getName: (item: T) => string): AlphabeticalEntry<T>[] {
  const entries: AlphabeticalEntry<T>[] = [];
  const specialItems: T[] = [];
  let currentLetter = '';

  items.forEach(item => {
    const firstLetter = Common.normalize(getName(item)).charAt(0).toUpperCase();
    if (!ALPHABETICAL_FIRST_LETTER.test(firstLetter)) {
      specialItems.push(item);
    } else {
      if (firstLetter !== currentLetter) {
        currentLetter = firstLetter;
        entries.push({ type: 'separator', value: currentLetter });
      }
      entries.push({ type: 'item', value: item });
    }
  });

  if (specialItems.length > 0) {
    specialItems.sort((a, b) => Common.normalize(getName(a)).localeCompare(Common.normalize(getName(b))));
    return [
      { type: 'separator', value: '#' },
      ...specialItems.map(item => ({ type: 'item', value: item }) as AlphabeticalEntry<T>),
      ...entries,
    ];
  }
  return entries;
}

/**
 * Rebuild letter groups from the incrementally-loaded slice of a flat entry
 * list, so infinite scroll keeps feeding a flat list while the template
 * renders design-system list categories.
 */
export function groupAlphabetical<T>(
  entries: AlphabeticalEntry<T>[],
  limit: number,
): { letter: string; items: T[] }[] {
  const groups: { letter: string; items: T[] }[] = [];
  entries.slice(0, limit).forEach(entry => {
    if (entry.type === 'separator') {
      groups.push({ letter: entry.value, items: [] });
    } else {
      groups[groups.length - 1]?.items.push(entry.value);
    }
  });
  return groups;
}
